/**
 * Path Social → Outfame migration helpers.
 *
 * Shared by the gift-reveal landing page (/gift) and the /setup migration banner.
 * Param contract (carried by our own redirect/email links only):
 *   ref=ps | utm_campaign=path_social_rebrand | ps_l=…      → migration visitor (banner gate)
 *   plan=core|elite|expert                                    → their Path Social plan tier
 *   price=…                                                   → optional explicit price string override
 *   username / email / coupon                                 → prefills /setup already reads
 */

export type PsPlanSlug = "core" | "elite" | "expert"

export interface PsPlanMapping {
    /** Display name of their Path Social plan (banner continuity copy). */
    psName: string
    /** The Outfame plan their PS tier maps to. */
    outfamePlan: "Go" | "Grow" | "Glory"
    /** Outfame yearly per-month price for the mapped plan ($/mo billed yearly), as shown on /pricing. */
    yearlyPrice: number
    /** Their Path Social per-month price when billed annually (banner price-honoring copy). Null = unknown. */
    psYearlyPrice: number | null
}

const PLAN_MAP: Record<string, PsPlanMapping> = {
    core: { psName: "Core", outfamePlan: "Go", yearlyPrice: 29, psYearlyPrice: 29 },
    elite: { psName: "Elite", outfamePlan: "Grow", yearlyPrice: 41, psYearlyPrice: 41 },
    expert: { psName: "Expert", outfamePlan: "Grow", yearlyPrice: 41, psYearlyPrice: null }
}

/** Normalize any plan param spelling (core, Core, core-annual, go…) to a mapping. Unknown → Go. */
export function mapPsPlan(planParam: string | null | undefined): PsPlanMapping {
    if (!planParam) return PLAN_MAP.core
    const slug = planParam.trim().toLowerCase().split("-")[0]
    return PLAN_MAP[slug] ?? PLAN_MAP.core
}

/** Outfame offer (redirect-Worker links carry offer=go|grow|glory, no plan) → the Path Social tier it came from. */
const OFFER_PS_TIER: Record<string, PsPlanSlug> = { go: "core", grow: "elite", glory: "expert" }

/** Path Social tier for the banner: the `plan` param (gift links) wins, else derived from `offer` (Worker links). */
export function psPlanParam(params: { get: (key: string) => string | null }): string | null {
    return params.get("plan") || OFFER_PS_TIER[(params.get("offer") || "").trim().toLowerCase()] || null
}

/** Is this visitor arriving from the Path Social migration flow? (banner gate) */
export function isMigrationVisit(params: {
    get: (key: string) => string | null
    has?: (key: string) => boolean
}): boolean {
    if (params.get("utm_campaign") === "path_social_rebrand") return true
    if (params.get("ref") === "ps") return true
    if (params.has?.("ps_l")) return true
    return false
}

/**
 * Express checkout for migration visitors (/setup only).
 *
 * Migration links (Path Social redirect Worker, /gift CTA) already carry the handle, email,
 * plan and coupon, so /setup runs a shortened flow for them:
 *   account-found reveal (loading overlay + analysis) → card form with the plan preselected,
 * with the account created silently from the URL email and the password set after purchase.
 * Visitors without the migration params get null here and the normal flow, unchanged.
 *
 * Plan: `offer=go|grow|glory` (Worker links) wins; otherwise the Path Social `plan` tier
 * (gift links) via mapPsPlan. Period: Yearly — the carried-over Path Social price the banner
 * promises is the annual one (Core $29/mo = Go yearly, Elite $41/mo = Grow yearly,
 * Expert = Glory yearly), and the psexpert coupon only exists on Glory yearly.
 */
export interface MigrationExpress {
    planName: "Go" | "Grow" | "Glory"
    period: "Yearly"
}

const OFFER_PLAN: Record<string, MigrationExpress["planName"]> = { go: "Go", grow: "Grow", glory: "Glory" }

export function getMigrationExpress(params: {
    get: (key: string) => string | null
    has?: (key: string) => boolean
}): MigrationExpress | null {
    if (!isMigrationVisit(params)) return null
    const offer = (params.get("offer") || "").trim().toLowerCase()
    const planName = OFFER_PLAN[offer] ?? mapPsPlan(params.get("plan")).outfamePlan
    return { planName, period: "Yearly" }
}

/** sessionStorage key: set when express created the account with a generated password (cleared once the customer sets theirs). */
export const EXPRESS_PASSWORD_PENDING_KEY = "outfame-express-password-pending"

/** Generated placeholder password for the silent express signup (never shown; replaced post-purchase). */
export function generateExpressPassword(): string {
    const bytes = new Uint8Array(24)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
}
